import React, { useEffect, useState } from "react";
import fetch from "./shared/Fetch";
import { format } from "./shared/NumberUtil";
import AppHeading from "./sharedComponents/AppHeading";
import Spinner from "./sharedComponents/Spinner";
import styled from "styled-components";
import FilteringBar from "./sharedComponents/FilteringBar";
import { SumData, WeekData } from "./Consultants";
import { getLast13Months } from "./shared/TimeUtil";
import { PageHeading } from "./Reconciliation";
import withSorting from "./shared/withSorting";
import TooltipLegend from "./sharedComponents/Tooltip";
import {
  FilteringBarContainer,
  FilterSidebarContainer,
  FilterIconStyled,
  FilterButton,
  CloseButton,
  CloseIconStyled,
  MainLayout
} from "./sharedComponents/FilterSidebar";

const ReconContainer = styled.div`
  padding: 0 5rem 5rem;
  width: 100%;
`;

const ConsultantName = styled.td`
  padding: 0.5rem;
  width: 15%;

  @media (max-width: 900px) {
    width: 30%;
  }

  @media (max-width: 750px) {
    padding: 0;
    font-size: 0.8rem;
  }
`;

const LeadingHeading = styled.th`
  padding: 0.5rem;
  text-align: left;
`;

const ReportTable = styled.table`
  border-collapse: collapse;
  margin-top: 30px;

  td {
    position: relative;
  }
  td:hover {
    outline: 1px solid #000;
    outline-offset: -1px;
    z-index: 1;
  }
`;

const ConsultantRow = styled.tr`
  &:nth-child(2n) {
    background: var(--table-band-background);
  }

  &:hover {
    background: var(--table-hover-background);
  }
`;

const SumRow = styled.tr`
  font-weight: bold;
`;

const Actuals = styled.div`
  color: ${props => (props.warning ? "red" : "DodgerBlue")};
`;

const Staffed = styled.div`
  color: black;
`;

const BarContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LeftButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const SeniorityLabel = styled.div`
  font-size: 0.75rem;
  color: var(--color-lightergrey);
  margin-top: 0.1rem;
`;

const Month = styled.th`
  text-align: right;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SumFooter = styled.tfoot`
  position: sticky;
  bottom: 0px;
  background: #fff;
`;

const ReconCell = ({ week }) => {
  const actuals = week.actuals ? week.actuals : 0;
  const staffed = week.staffed ? week.staffed : 0;
  let warning = true;
  if (staffed === 0 || actuals / staffed > 0.9) warning = false;

  return (
    <WeekData className="tooltip">
      <Staffed>{format(staffed)}</Staffed>
      <Actuals warning={warning}>{format(actuals)}</Actuals>
      <TooltipLegend
        items={[
          { value: format(staffed), label: "Planlagt fakturert" },
          { value: format(actuals), label: "Faktisk fakturert" }
        ]}
      />
    </WeekData>
  );
};

const ReconLine = ({ cons, seniorityById }) => {
  const weekCells = [];
  for (const [key, value] of Object.entries(cons.months)) {
    if (value.totalWorkingHours === 0) {
      weekCells.push(
        <WeekData key={key} className="tooltip">
          <Staffed />
          <Actuals warning={true} />
          <TooltipLegend
            items={[
              { value: format(0), label: "Planlagt fakturert" },
              { value: format(0), label: "Faktisk fakturert" }
            ]}
          />
        </WeekData>
      );
      continue;
    }
    weekCells.push(<ReconCell key={key} week={value} />);
  }

  return (
    <ConsultantRow>
      <ConsultantName>
        {cons.name}
        {(cons.seniorityId || cons.seniorityOrderId) &&
          seniorityById[cons.seniorityId || cons.seniorityOrderId] && (
            <SeniorityLabel>
              {seniorityById[cons.seniorityId || cons.seniorityOrderId]}
            </SeniorityLabel>
          )}
      </ConsultantName>
      {weekCells}
    </ConsultantRow>
  );
};

const ReconciliationReport = props => {
  const [data, setData] = useState([]);
  const [waitingForData, setWaitingForData] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [seniorityById, setSeniorityById] = useState({});
  const [groupServiceAreaMap, setGroupServiceAreaMap] = useState({});

  const { sortBy, sortDirection, handleSort, sortItems } = props;
  const weeks = props.match.params.weeks;
  const offset = props.match.params.offset;
  const departments = props.match.params.departments;
  const groups = props.match.params.groups;
  const availableOnly = props.match.params.availableOnly;
  const seniorities = props.match.params.seniorities;
  const serviceareas = props.match.params.serviceareas;

  const fetchData = async () => {
    setWaitingForData(true);
    try {
      const response = await fetch(`/reconciliation/report/`);
      setData(Object.values(response.consultants));
    } catch (e) {
      console.error("Failed to fetch reconciliation report data", e);
    } finally {
      setWaitingForData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch seniority names for toggle display
    (async () => {
      try {
        const res = await fetch(`/seniorities`);
        const map = {};
        if (Array.isArray(res)) {
          res.forEach(s => {
            map[s.id] = s.name;
          });
        }
        setSeniorityById(map);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/groups`);
        const map = {};
        if (Array.isArray(res)) {
          res.forEach(g => {
            map[g.id] = g.serviceAreaId;
          });
        }
        setGroupServiceAreaMap(map);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    setWaitingForData(false);
  }, [serviceareas, groups]);

  const departmentsArray = departments === "all" ? [] : departments.split(",");
  const groupsArray = groups === "all" ? [] : groups.split(",");
  const senioritiesArray = seniorities === "all" ? [] : seniorities.split(",");
  const serviceareasArray =
    serviceareas === "all" ? [] : serviceareas ? serviceareas.split(",") : [];

  const sp = new URLSearchParams(props.location.search || "");
  const sval = key => (sp.get(key) || "").toLowerCase();
  const wantVacation = sval("vacation") === "true";
  const wantOnLeave = sval("onleave") === "true";
  const wantOverbooked = sval("overbooked") === "true";
  const wantHideFullAbsence = sval("hideFullAbsence") === "true";

  const filteredData = data
    .filter(element => {
      return departmentsArray.some(op => {
        return op === element.departmentId;
      });
    })
    .filter(element => {
      if (groupsArray.length === 0) return true;
      return element.groups.some(g => groupsArray.some(a => a === g.id));
    })
    .filter(element => {
      if (senioritiesArray.length === 0) return true;
      if (!element.seniorityOrderId) return false;
      return senioritiesArray.some(
        s => s === element.seniorityOrderId.toString()
      );
    })
    .filter(element => {
      if (serviceareasArray.length === 0) return true;
      return element.groups.some(g =>
        serviceareasArray.some(a => a === groupServiceAreaMap[g.id])
      );
    })
    .filter(element => {
      if (!(wantVacation || wantOnLeave || wantOverbooked)) return true;
      const months = element.months ? Object.values(element.months) : [];
      const hasVacation = months.some(m => (m.vacationHours || 0) > 0);
      const hasLeave = months.some(m => (m.plannedAbsenceHours || 0) > 0);
      const hasOver = months.some(
        m =>
          (parseFloat(m.staffed) || 0) > (parseFloat(m.totalWorkingHours) || 0)
      );
      if (wantVacation && !hasVacation) return false;
      if (wantOnLeave && !hasLeave) return false;
      if (wantOverbooked && !hasOver) return false;
      return true;
    })
    .filter(element => {
      if (!wantHideFullAbsence) return true;
      const months = element.months ? Object.values(element.months) : [];
      const relevantMonths = months.filter(m => (m.totalWorkingHours || 0) > 0);
      if (relevantMonths.length === 0) return true;
      return !relevantMonths.every(
        m =>
          (m.vacationHours || 0) + (m.plannedAbsenceHours || 0) >=
          (m.totalWorkingHours || 0)
      );
    });

  const sortedData = sortItems(filteredData);
  const sums = [];
  for (const row of sortedData) {
    let i = 0;
    for (const w of Object.values(row.months)) {
      if (!sums[i]) {
        sums[i] = {
          actuals: 0,
          staffed: 0,
          totalWorkHours: 0,
          plannedAbsenceHours: 0,
          vacationHours: 0
        };
      }
      sums[i].actuals += w.actuals ? w.actuals : 0;
      sums[i].staffed += w.staffed ? w.staffed : 0;
      sums[i].totalWorkHours += w.totalWorkingHours ? w.totalWorkingHours : 0;
      sums[i].plannedAbsenceHours += w.plannedAbsenceHours
        ? w.plannedAbsenceHours
        : 0;
      sums[i].vacationHours += w.vacationHours ? w.vacationHours : 0;
      i++;
    }
  }
  const consultantRow = sortedData.map(cons => {
    return (
      <ReconLine key={cons.id} cons={cons} seniorityById={seniorityById} />
    );
  });

  const yearMonths = getLast13Months();

  return (
    <div className="module">
      <AppHeading
        departments={departments}
        offset={offset}
        weeks={weeks}
        groups={groups}
        availableOnly={availableOnly}
        seniorities={seniorities}
        serviceareas={serviceareas}
        active={"reconciliation"}
      />
      <FilterSidebarContainer isOpen={isFilterOpen}>
        <CloseButton onClick={() => setIsFilterOpen(false)}>
          <CloseIconStyled />
        </CloseButton>
        <FilteringBar
          selectedDepartments={departmentsArray}
          selectedGroups={groupsArray}
          selectedSeniorities={senioritiesArray}
          selectedServiceAreas={serviceareasArray}
          onNavigate={() => setWaitingForData(true)}
        />
      </FilterSidebarContainer>
      <MainLayout sidebarOpen={isFilterOpen}>
        <ReconContainer>
          <PageHeading
            departments={departments}
            offset={offset}
            weeks={weeks}
            groups={groups}
            availableOnly={availableOnly}
            seniorities={seniorities}
            serviceareas={serviceareas}
          />
          <Spinner show={waitingForData} />
          <h3 className="page-heading">Avstemmingsrapport</h3>
          <BarContainer>
            <LeftButtons>
              <FilteringBarContainer>
                <FilterButton
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  aria-label="Åpne filter"
                  title="Åpne filter"
                >
                  <FilterIconStyled />
                </FilterButton>
              </FilteringBarContainer>
            </LeftButtons>
          </BarContainer>
          {!waitingForData && (
            <ReportTable>
              <thead>
                <tr className="fixedHeading">
                  <LeadingHeading>
                    {["Antall: " + filteredData.length]}
                  </LeadingHeading>
                  {yearMonths.map((month, i) => (
                    <Month key={i} onClick={() => handleSort(i)}>
                      {month}{" "}
                      {sortBy === i && (sortDirection === "desc" ? "▼" : "▲")}
                    </Month>
                  ))}
                </tr>
              </thead>
              <tbody>{consultantRow}</tbody>
              <SumFooter>
                <SumRow className="line">
                  <ConsultantName>Sum bemannet</ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>{format(sum.staffed)}</SumData>
                  ))}
                </SumRow>
                <SumRow>
                  <ConsultantName>Sum levert</ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>{format(sum.actuals)}</SumData>
                  ))}
                </SumRow>
                <SumRow>
                  <ConsultantName>Sum fraværstimer</ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>{format(sum.plannedAbsenceHours)}</SumData>
                  ))}
                </SumRow>
                <SumRow>
                  <ConsultantName>Sum ferietimer</ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>{format(sum.vacationHours)}</SumData>
                  ))}
                </SumRow>
                <SumRow>
                  <ConsultantName>Leveringgrad</ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>
                      {format((sum.actuals / sum.staffed) * 100)}%
                    </SumData>
                  ))}
                </SumRow>
                <SumRow>
                  <ConsultantName title="Faktisk faktureringsgrad">
                    Faktisk faktureringsgrad
                  </ConsultantName>
                  {sums.map((sum, i) => (
                    <SumData key={i}>
                      {format(
                        (sum.actuals /
                          (sum.totalWorkHours -
                            ((sum.plannedAbsenceHours || 0) +
                              (sum.vacationHours || 0)))) *
                          100
                      )}
                      %
                    </SumData>
                  ))}
                </SumRow>
              </SumFooter>
            </ReportTable>
          )}
        </ReconContainer>
      </MainLayout>
    </div>
  );
};

export default withSorting(ReconciliationReport, {
  getSortValue: (item, columnIndex) => {
    // Sort by delivered (actuals) hours for the selected month index
    const monthsArray = Object.values(item.months || {});
    const month = monthsArray[columnIndex] || {};
    const val = month && month.actuals != null ? parseFloat(month.actuals) : 0;
    return isNaN(val) ? 0 : val;
  },
  getDefaultSortValue: item => item.name
});
